import { differenceInDays } from 'date-fns';
import type { StructuredTextGraphQlResponse } from 'react-datocms';
import { parseDate } from 'utils/DateFormats';
import type { JourneyGuidePlaceRecordFragment } from '~/libs/dato/fragments/journeyGuidePlaceFragment';
import type { LegBookingRecordFragment } from '~/libs/dato/fragments/legBookingFragment';
import type { StayBookingRecordFragment } from '~/libs/dato/fragments/stayBookingFragment';
import type { StopRecordFragment } from '~/libs/dato/fragments/stopFragment';
import type { JourneyGuideItems } from '~/libs/dato/queries/journeyGuide';
import type { Transport } from '~/types/JourneyItem.types';
import { slugify } from '~/utils';

type Content = {
  content: StructuredTextGraphQlResponse;
  category: 'alert' | 'info' | 'timetabling';
  title: string;
};

type FoodAndDrinkRecommendation = {
  description: StructuredTextGraphQlResponse | null;
  title: string | null;
} | null;

interface BaseTimelineItem {
  id: string;
  startDate: string | null;
  endDate: string | null;
}

export interface LegTimelineItem extends BaseTimelineItem {
  type: 'Journey';
  origin: string;
  transportClass: string;
  originTerminalName: string | null;
  originGeoCoordinates: { latitude: number; longitude: number } | null;
  destination: string;
  destinationTerminalName: string | null;
  destinationGeoCoordinates: { latitude: number; longitude: number } | null;
  nights: number | null;
  departureTime: string | null;
  arrivalTime: string | null;
  estimatedMinutes: number | null;
  modeOfTransports: Transport[];
  paymentStatus: string | null;
  bookingReference: string | null;
  keyInformation: string | null;
  goodToKnowAutomatedContent: Content[];
  ticketType: string;
  printTicket: boolean | null;
  tickets: { title: string | null; url: string }[];
  leg: LegBookingRecordFragment['leg'];
}

export interface StayTimelineItem extends BaseTimelineItem {
  type: 'Stay';
  title: string;
  nights: number;
  geoCoordinates: { latitude: number; longitude: number } | null;
  place: JourneyGuidePlaceRecordFragment;
  accommodationBookings: StayBookingRecordFragment['accommodation'];
  bagDrops: any[] | null;
  activities: StayBookingRecordFragment['activities'] | null;
  foodAndDrinkRecommendation: FoodAndDrinkRecommendation;
  eateries: StayBookingRecordFragment['eateries'] | null;
  slug: string;
  userSpecificDescription: string | null;
}

export interface StopTimelineItem extends BaseTimelineItem {
  type: 'Stop';
  title: string;
  nights: number;
  geoCoordinates: { latitude: number; longitude: number } | null;
  place: JourneyGuidePlaceRecordFragment;
  bagDrops: any[] | null;
  activities: StopRecordFragment['activities'] | null;
  foodAndDrinkRecommendation: FoodAndDrinkRecommendation;
  eateries: StopRecordFragment['eateries'] | null;
}

export type TimelineItem = LegTimelineItem | StayTimelineItem | StopTimelineItem;

export interface TransformedTimeline {
  timelineItems: TimelineItem[];
  headerImage: any | null;
}

const numberNights = (startDate: string | null, endDate: string | null): number => {
  if (!startDate || !endDate) return 0;
  return differenceInDays(parseDate(endDate), parseDate(startDate));
};

export default class TimelineTransformer {
  private data: JourneyGuideItems;

  constructor(data: JourneyGuideItems) {
    this.data = data;
  }

  transform(): TransformedTimeline {
    const items = this.data;

    // Find header image
    // logic: use photo assigned to Place with most nights
    // If most nights is equal in more than one Place, use the first Place visited for that number of nights
    const stayImages = items
      .filter((item): item is StayBookingRecordFragment => item.__typename === 'StayRecord')
      .map((stay, i) => ({
        index: i,
        duration: numberNights(stay.startDate, stay.endDate),
        image: stay.place.images[0]?.responsiveImage || null
      }))
      .sort((a, b) => b.duration - a.duration || a.index - b.index);

    const ticketTypes = (type: string | null) => {
      if (!type) return 'ticket';
      switch (true) {
        case /^ticket/i.test(type):
          return 'ticket';
        case /^reservation/i.test(type):
          return 'reservation';
        case /tod/i.test(type):
          return 'tod';
        case /eurostar/i.test(type):
          return 'eurostar';
        case /interrail/i.test(type):
          return 'interrail';
        case /day/i.test(type):
          return 'payOnDay';
        case /terminal/i.test(type):
          return 'payAtTerminal';
        case /none/i.test(type):
          return 'none';
        default:
          return 'ticket';
      }
    };

    const timelineItems = items.map((item) => {
      const tempBaseItem = {
        id: item.id,
        startDate: item.startDate,
        endDate: item.endDate
      };

      let tempItem = {};
      switch (item.__typename) {
        case 'LegBookingRecord':
          tempItem = {
            type: 'Journey',
            origin: item.origin,
            transportClass: item?.transportClass ?? '',
            originTerminalName: item.originTerminalName,
            originGeoCoordinates: item.originLocation,
            destination: item.destination,
            destinationTerminalName: item.destinationTerminalName,
            destinationGeoCoordinates: item.destinationLocation,
            nights: item.nights,
            departureTime: item.departureTime,
            arrivalTime: item.arrivalTime,
            estimatedMinutes: item.estimatedMinutes,
            modeOfTransports: item.modeOfTransports?.split(',') ?? [],
            paymentStatus: item.paymentStatus,
            bookingReference: item.bookingReference,
            keyInformation: item.keyInformation,
            goodToKnowAutomatedContent: item.goodToKnowAutomatedContent,
            ticketType: ticketTypes(item.ticketType),
            printTicket: item.printTicket,
            tickets: item.tickets,
            leg: item.leg
          };
          break;
        case 'StayRecord':
          tempItem = {
            type: 'Stay',
            title: item.place.placeName,
            nights: numberNights(item.startDate, item.endDate),
            geoCoordinates: item.place.location,
            place: item.place,
            accommodationBookings: item.accommodation,
            bagDrops: item.bagDrops,
            activities: item.activities,
            foodAndDrinkRecommendation: item.foodAndDrinkRecommendation,
            eateries: item.eateries,
            slug: slugify(item.place.placeName),
            userSpecificDescription: item.userSpecificDescription || null
          };
          break;
        case 'StopRecord':
          tempItem = {
            type: 'Stop',
            title: item.place?.placeName,
            nights: 0,
            geoCoordinates: item.place?.location,
            place: item.place,
            bagDrops: item.bagDrops,
            activities: item.activities,
            foodAndDrinkRecommendation: item.foodAndDrinkRecommendation,
            eateries: item.eateries
          };
          break;
      }

      return {
        ...tempBaseItem,
        ...tempItem
      } as TimelineItem;
    });
    return {
      timelineItems,
      headerImage: stayImages?.[0]?.image
    };
  }
}
